import { computed, watch } from 'vue';
import { useConvoStore } from 'src/stores/conversations';
import { useLoggingStore } from 'src/stores/logging';
import date from 'quasar/src/utils/date/date.js';
const { getDateDiff } = date;
import { from, of, zip } from 'rxjs';
import { catchError, map, mergeMap, retry, scan, switchMap, tap } from 'rxjs/operators';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import { v4 as uuidv4 } from 'uuid';
import { parseCamelCase } from 'src/plugins/string/string';
import { ChatMiscDataPlugin } from 'src/features/messaging/chat/components/methods/misc';
/**
 * Plugin for chat msg sending
 *
 * @param updateTyping function to update typing status
 * @param isFirstMsg if this msg is the first msg
 * @param lastMsg check for previous message
 * @param replied if we replied to the previous msg
 * @returns
 */
const ChatSendMsgPlugin = (updateTyping, isFirstMsg, lastMsg, replied) => {
    const { user, convo, appType, receiver } = ChatMiscDataPlugin();
    const convoStore = useConvoStore();
    const loggingStore = useLoggingStore();
    const sending = computed({
        get: () => {
            return convoStore.sending;
        },
        set: (newValue) => {
            convoStore.updateSending(newValue);
        }
    });
    const { subject: sendMessage$ } = rxInitSubjectCallback();
    /**
     * Send message
     * @param sendData
     */
    function consolidateSend(sendData) {
        sendData.app_type = appType.value;
        // if there is last message, get the reply time
        if (lastMsg.value) {
            if ((Number(lastMsg.value.sender) !== Number(user.value.user_pk)) && !replied.value) {
                const differenceInSeconds = getDateDiff(new Date(), lastMsg.value.createdAt.toDate(), 'seconds');
                sendData.replyTime = differenceInSeconds;
            }
        }
        updateTyping(false);
        sendMessage$.next(sendData);
    }
    useObservable(sendMessage$.pipe(switchMap(convoData => {
        return of('').pipe(switchMap(() => {
            return import(/* webpackChunkName: "firebaseMethod" */ 'src/_helpers/firebaseMethods');
        }), map(({ default: firebaseMethods }) => firebaseMethods), switchMap(firebaseMethods => {
            return zip(firebaseMethods(), import(
            /* webpackChunkName: "firebaseMethod" */
            'firebase/firestore'));
        }), switchMap(([{ db }, { doc: fbDoc, setDoc, updateDoc, serverTimestamp: fbServerTimestamp, increment: fbIncrement }]) => {
            sending.value = true;
            const data = { ...convoData };
            const convoRef = fbDoc(db, 'chat', data.conversationId);
            const notifRef = fbDoc(db, 'notification', data.receiver.toString());
            const currentDate = new Date().toISOString();
            const messageKeys = [
                'app_type',
                'type',
                'text',
                'dimensions',
                'ending',
                'filename',
                'url',
                'custom_order_id',
                'listingDetails',
                'status',
                'first_msg',
                'sender',
            ];
            const messageData = {
                // @ts-expect-error ignore. use server timestamp here for better accuracy
                createdAt: fbServerTimestamp(),
                created: currentDate,
                updated: currentDate,
                read: '',
                notified: false,
                whatsapp_notified: '',
                chatgpt_chked: '',
                ...(isFirstMsg.value ? {
                    first_msg: true
                } : {})
            };
            for (const key in data) {
                const typedKey = key;
                if (messageKeys.indexOf(typedKey) === -1)
                    continue;
                // @ts-expect-error ignore for generic object loop to get data
                messageData[typedKey] = data[typedKey];
            }
            let hasError = false;
            /**
             * Create message -> update chatroom -> update notification
             *
             * in sequence
             */
            return from([{
                    'type': 'set',
                    'ref': fbDoc(db, `${'chat'}/${data.conversationId}/messages/${uuidv4()}`),
                    'data': messageData
                }, {
                    'type': 'update',
                    'ref': convoRef,
                    'data': {
                        [`unread_${data.receiver}`]: fbIncrement(1),
                        [`last_active_${data.sender}`]: currentDate,
                        'message_count': fbIncrement(1),
                        'last_active': currentDate,
                        'last_message': ((data.type === 'text') || data.text)
                            ? data.text
                            : parseCamelCase(data.type, '_'),
                        'last_sender': data.sender,
                        'last_message_date': currentDate,
                        'updated': new Date().toISOString(),
                        ...(data.replyTime ? {
                            [`replyTime_${data.sender}`]: fbIncrement(data.replyTime),
                            [`replyCount_${data.sender}`]: fbIncrement(1)
                        } : {})
                    }
                }, {
                    'type': 'update',
                    'ref': notifRef,
                    'data': {
                        unread_chat: fbIncrement(1),
                        updated: new Date().toISOString()
                    }
                }]).pipe(mergeMap((v) => {
                if (v.type === 'set') {
                    return setDoc(v.ref, v.data);
                }
                return updateDoc(v.ref, v.data);
            }), retry(3), catchError((err) => {
                sending.value = false;
                hasError = true;
                loggingStore.errorHandler(err);
                return of(err);
            }), tap(() => {
                // throw error if there is error
                if (hasError) {
                    throw new Error('Error sending message');
                }
            }), scan((total) => total += 1, 0), tap(v => {
                if (v < 3)
                    return;
                sending.value = false;
            }));
        }));
    })));
    /**
     * Send text message
     * @param text Text msg
     */
    function sendTxt(text) {
        consolidateSend({
            type: 'text',
            text: text,
            url: [],
            ending: [],
            filename: [],
            conversationId: convo.value.id,
            sender: Number(user.value.user_pk),
            receiver: Number(receiver.value)
        });
    }
    /**
     * If there is initialize message, send it first
     */
    const initMsg = computed(() => convoStore.initMsg);
    watch(initMsg, (newValue) => {
        if (!newValue)
            return;
        sendTxt(newValue);
        convoStore.clearInit();
    }, { immediate: true });
    return {
        consolidateSend,
        sendTxt
    };
};
export { ChatSendMsgPlugin };
